/**
 * briefNextAction — the one sentence and the one button every brief screen
 * opens with.
 *
 * A brief is a question with a deadline, one owner, and exactly one next
 * action at any moment. This module computes that action from the rows
 * (never from UI state) so the list row, the sticky bar on the decision
 * page, and any future card all say the same thing. C Mode rule 4: the ball
 * is always named.
 */

import type { Brief, BriefDecision, BriefOptionWithProposal, BriefStatus } from '../../../types/brief';

export type BriefStage = 'setup' | 'proposals' | 'compare' | 'decide' | 'share';

export interface BriefNextAction {
  /** Where the ball is, in plain words: "you", "Priya", "3 proposal owners", "nobody — it's decided". */
  ballWith: string;
  /** One sentence for the current state, e.g. "3 of 4 proposals in · due Friday". */
  status: string;
  /** The single primary action, or null when there is nothing for the viewer to do. */
  primary: { label: string; stage: BriefStage } | null;
  /** The stage that should be expanded when the page opens. */
  liveStage: BriefStage;
  /** Whether the viewer is the person the ball is with. */
  needsViewer: boolean;
}

export interface NextActionInput {
  brief: Pick<Brief, 'status' | 'deadline' | 'created_by' | 'decision_authority' | 'promoted_decision_id'>;
  options: Array<Pick<BriefOptionWithProposal, 'name' | 'owner_name' | 'proposal'>>;
  criteriaCount: number;
  decision: Pick<BriefDecision, 'selected_option_ids' | 'revisit_when'> | null;
  viewerId: string | null;
  /** Injectable for tests. */
  today?: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "due today", "due tomorrow", "due in 3 days", "due Fri 3 Oct", "2 days overdue". */
export function dueLabel(deadline: string | null, today: Date = new Date()): string | null {
  if (!deadline) return null;
  const due = new Date(`${deadline}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due.getTime() - start.getTime()) / DAY_MS);
  if (days < -1) return `${-days} days overdue`;
  if (days === -1) return '1 day overdue';
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  if (days <= 6) return `due in ${days} days`;
  return `due ${due.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`;
}

function withDue(text: string, due: string | null): string {
  return due ? `${text} · ${due}` : text;
}

export function computeBriefNextAction(input: NextActionInput): BriefNextAction {
  const { brief, options, criteriaCount, decision, viewerId } = input;
  const isCreator = Boolean(viewerId) && brief.created_by === viewerId;
  const named = options.filter((o) => o.name.trim().length > 0);
  const submitted = named.filter((o) => o.proposal !== null).length;
  const outstanding = named.filter((o) => o.proposal === null);
  const due = dueLabel(brief.deadline, input.today);
  const decider = brief.decision_authority?.trim() || (isCreator ? 'you' : 'the decider');
  const status: BriefStatus = brief.status;

  if (status === 'draft') {
    const ready = named.length >= 2 && criteriaCount >= 1;
    const missing: string[] = [];
    if (named.length < 2) missing.push(named.length === 0 ? 'add at least two options' : 'add one more option');
    if (criteriaCount < 1) missing.push('say what matters');
    return {
      ballWith: isCreator ? 'you' : 'the person setting it up',
      status: withDue(ready ? 'Ready to open for proposals' : `Still setting up — ${missing.join(', ')}`, due),
      primary: isCreator ? { label: ready ? 'Open for proposals' : 'Finish setting up', stage: 'setup' } : null,
      liveStage: 'setup',
      needsViewer: isCreator,
    };
  }

  if (status === 'collecting') {
    const allIn = named.length > 0 && outstanding.length === 0;
    const owners = outstanding.map((o) => o.owner_name?.trim()).filter((n): n is string => Boolean(n));
    const ballWith = allIn
      ? isCreator ? 'you' : decider
      : owners.length === 1
        ? owners[0]
        : `${outstanding.length} proposal owner${outstanding.length === 1 ? '' : 's'}`;
    return {
      ballWith,
      status: withDue(
        allIn ? `All ${named.length} proposals are in` : `${submitted} of ${named.length} proposals in`,
        due,
      ),
      primary: isCreator
        ? allIn
          ? { label: 'Start comparing', stage: 'compare' }
          : { label: submitted > 0 ? 'Compare what is in' : 'Remind the owners', stage: 'proposals' }
        : null,
      liveStage: 'proposals',
      needsViewer: isCreator && allIn,
    };
  }

  if (status === 'comparing') {
    return {
      ballWith: isCreator ? 'you' : decider,
      status: withDue(`Comparing ${submitted} proposal${submitted === 1 ? '' : 's'}`, due),
      primary: isCreator ? { label: 'Decide', stage: 'decide' } : null,
      liveStage: 'compare',
      needsViewer: isCreator,
    };
  }

  if (status === 'decided') {
    const selected = decision?.selected_option_ids?.length ?? 0;
    const onPlan = Boolean(brief.promoted_decision_id);
    return {
      ballWith: 'nobody — it is decided',
      status: selected > 0
        ? `Decided · ${selected} option${selected === 1 ? '' : 's'} chosen${onPlan ? ' · on the plan' : ''}`
        : `Decided · nothing chosen${onPlan ? ' · on the plan' : ''}`,
      primary: isCreator && !onPlan ? { label: 'Share the record', stage: 'share' } : null,
      liveStage: 'share',
      needsViewer: false,
    };
  }

  if (status === 'deferred') {
    const when = decision?.revisit_when?.trim();
    return {
      ballWith: isCreator ? 'you, later' : decider,
      status: when ? `Deferred until ${when}` : 'Deferred',
      primary: isCreator ? { label: 'Reopen when it is time', stage: 'decide' } : null,
      liveStage: 'decide',
      needsViewer: false,
    };
  }

  return {
    ballWith: 'nobody — it is archived',
    status: 'Archived',
    primary: null,
    liveStage: 'share',
    needsViewer: false,
  };
}
